// copy_controller.js
// Click-to-copy for a value the reader is meant to paste somewhere else — today the MCP address and the
// CLI commands in the AI-assistant help articles, via {% ruby_render "common/copy_field", value: "…" %}.
// The value lives in a data attribute rather than being read back out of the DOM, so the text on screen
// can be shortened or styled without changing what lands on the clipboard.
import { Controller } from '@hotwired/stimulus'

const CONFIRM_MS = 2000

export default class extends Controller {
  static targets = ['button', 'status']
  static values = { text: String }

  disconnect() {
    if (this.timeout) clearTimeout(this.timeout)
  }

  copy() {
    // navigator.clipboard is undefined outside a secure context (plain http on a LAN IP, say),
    // so fall back to the old selection-based copy rather than leaving the button dead.
    if (navigator.clipboard) {
      navigator.clipboard.writeText(this.textValue).then(
        () => this.confirm(),
        () => this.fallback(),
      )
    } else {
      this.fallback()
    }
  }

  fallback() {
    const field = document.createElement('textarea')
    field.value = this.textValue
    field.setAttribute('readonly', '')
    field.style.position = 'fixed'
    field.style.opacity = '0'
    document.body.appendChild(field)
    field.select()
    try {
      document.execCommand('copy')
      this.confirm()
    } catch (e) {
      /* nothing we can do — the reader can still select the text by hand */
    }
    document.body.removeChild(field)
  }

  // The button shows a copy icon that flips to a checkmark for a moment. The swap itself is CSS on
  // [data-copied]; the label and the live region carry the same confirmation to screen readers.
  confirm() {
    if (!this.hasButtonTarget) return
    if (this.timeout) clearTimeout(this.timeout)
    this.buttonTarget.dataset.copied = 'true'
    this.buttonTarget.setAttribute('aria-label', 'Kopiert!')
    if (this.hasStatusTarget) this.statusTarget.textContent = 'Kopiert!'
    this.timeout = setTimeout(() => {
      delete this.buttonTarget.dataset.copied
      this.buttonTarget.setAttribute('aria-label', 'Kopier')
      if (this.hasStatusTarget) this.statusTarget.textContent = ''
    }, CONFIRM_MS)
  }
}
